import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";

export default function SelectionToolbar({
    children,
    onAction,
    buttonText = "Ask",
    offset = 8,
    disabled = false,   // ✅ new prop
}) {
    const rootRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const isInside = (container, node) =>
        container && node && container.contains(node.nodeType === 3 ? node.parentNode : node);

    const getStartRect = (range) => {
        const r = range.cloneRange();
        r.collapse(true);
        const rects = r.getClientRects?.();
        if (rects && rects.length) return rects[0];
        return r.getBoundingClientRect();
    };

    const updatePosition = useCallback(() => {
        if (disabled) {   // ✅ don’t show at all if disabled
            setVisible(false);
            return;
        }

        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) return setVisible(false);

        const range = sel.rangeCount ? sel.getRangeAt(0) : null;
        if (!range) return setVisible(false);

        if (!isInside(rootRef.current, range.startContainer)) {
            return setVisible(false);
        }

        const startRect = getStartRect(range);
        if (!startRect || (!startRect.width && !startRect.height)) {
            return setVisible(false);
        }

        const top = window.scrollY + startRect.top - offset;
        const left = window.scrollX + startRect.left;
        setPos({ top, left });
        setVisible(true);
    }, [offset, disabled]);

    useEffect(() => {
        if (disabled) return; // ✅ don’t attach listeners if disabled

        const tick = () => setTimeout(updatePosition, 0);
        const collapseCheck = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed) setVisible(false);
        };

        document.addEventListener("mouseup", tick);
        document.addEventListener("keyup", tick);
        window.addEventListener("scroll", tick, true);
        window.addEventListener("resize", tick);
        document.addEventListener("selectionchange", collapseCheck);

        return () => {
            document.removeEventListener("mouseup", tick);
            document.removeEventListener("keyup", tick);
            window.removeEventListener("scroll", tick, true);
            window.removeEventListener("resize", tick);
            document.removeEventListener("selectionchange", collapseCheck);
        };
    }, [updatePosition, disabled]);

    const floating = !disabled && visible ? (
        <div
            style={{
                position: "absolute",
                top: pos.top,
                left: pos.left,
                transform: "translate(0, -100%)",
                zIndex: 9999,
                pointerEvents: "auto",
            }}
        >
            <Paper elevation={3} style={{ borderRadius: 999, overflow: "hidden" }}>
                <Button
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                        e.preventDefault();
                        const sel = window.getSelection();
                        onAction?.(sel ? sel.toString() : "");
                    }}
                    sx={{ textTransform: "none", px: 1.5, py: 0.5, fontSize: 12 }}
                >
                    {buttonText}
                </Button>
            </Paper>
        </div>
    ) : null;

    return (
        <div ref={rootRef} style={{ position: "relative" }}>
            {children}
            {createPortal(floating, document.body)}
        </div>
    );
}
